// usePreloadActiveBase.js
import { useEffect, useRef } from 'react';

// Context
import { useStartupStatus } from 'context/StartupStatusContext';
import { useUser } from 'context/UserContext';
import { useBase } from 'context/BaseContext';
import { useBaseRoster } from 'context/BaseRosterContext';

import {
  getInitialBaseForUser,
  getBaseKey,
  subscribeToBase,
} from 'services/bases/BaseReadService';
import { getActiveBaseKeyFromSettings } from 'utilities/baseSettingsKeys';

const getPreferredBaseKey = (userSettings) => getActiveBaseKeyFromSettings(userSettings);

const applyResolvedBase = (previousBase, nextBase) => {
  if (getBaseKey(previousBase)) {
    return previousBase;
  }

  if (!nextBase) {
    return previousBase || null;
  }

  const baseKey = getBaseKey(nextBase);
  return baseKey ? { ...nextBase, key: baseKey } : (previousBase || null);
};

export function pickRestoredBase(bases, preferredBaseKey = '') {
  const list = Array.isArray(bases) ? bases.filter(Boolean) : [];
  if (!list.length) return null;

  const cleanedPreferredBaseKey = String(preferredBaseKey || '').trim();
  if (cleanedPreferredBaseKey) {
    const preferredBase = list.find((base) => getBaseKey(base) === cleanedPreferredBaseKey);
    if (preferredBase) return preferredBase;
  }

  return list[0] || null;
}

const mergeBaseUpdate = (previousBase, nextBase, subscribedKey) => {
  if (nextBase) {
    const nextKey = getBaseKey(nextBase);
    return nextKey ? { ...nextBase, key: nextKey } : previousBase;
  }

  const previousKey = getBaseKey(previousBase);
  if (previousKey && previousKey === subscribedKey) {
    return previousBase;
  }

  return null;
};

/**
 * Bootstraps the initial active base once per authenticated session,
 * then keeps the active base doc live without re-running cold-start status.
 *
 * Last-selected base is stored as ACTIVE_BASE_KEY in user settings. Restore
 * prefers that key from the live roster (same list the rail uses), and falls
 * back to getInitialBaseForUser if the roster has not landed yet.
 */
const usePreloadActiveBase = () => {
  const {
    isEmailVerified,
    isUserSettingsLoading,
    user,
    userSettings,
  } = useUser();
  const { clearStatus, setStatus } = useStartupStatus();
  const { bases: rosterBases, hasLoaded: rosterHasLoaded } = useBaseRoster();

  const { activeBase, setActiveBase } = useBase();

  const activeBaseKeyRef = useRef('');
  const hasResolvedInitialBaseRef = useRef(false);

  useEffect(() => {
    activeBaseKeyRef.current = getBaseKey(activeBase);
  }, [activeBase]);

  useEffect(() => {
    if (!user?.key || !isEmailVerified) {
      hasResolvedInitialBaseRef.current = false;
      activeBaseKeyRef.current = '';
      clearStatus('startup.bases');
      setActiveBase(null);
      return undefined;
    }

    if (isUserSettingsLoading && !activeBaseKeyRef.current) {
      setStatus('startup.bases', {
        message: 'Waiting for your settings before choosing a base…',
        priority: 40,
      });
      return undefined;
    }

    if (isUserSettingsLoading) {
      clearStatus('startup.bases');
      return undefined;
    }

    if (hasResolvedInitialBaseRef.current || activeBaseKeyRef.current) {
      if (activeBaseKeyRef.current) {
        clearStatus('startup.bases');
      }
      return undefined;
    }

    let isDisposed = false;

    setStatus('startup.bases', {
      message: 'Loading your space…',
      priority: 40,
    });

    const preferredBaseKey = getPreferredBaseKey(userSettings);

    getInitialBaseForUser({
      userKey: user.key,
      preferredBaseKey,
    })
      .then((base) => {
        if (isDisposed) return;

        hasResolvedInitialBaseRef.current = true;

        setActiveBase((previous) => applyResolvedBase(previous, base));

        if (base || activeBaseKeyRef.current) {
          clearStatus('startup.bases');
        }
      })
      .catch((error) => {
        if (isDisposed) return;

        hasResolvedInitialBaseRef.current = true;
        console.error('[usePreloadActiveBase] failed to load initial base:', error);

        if (activeBaseKeyRef.current) {
          clearStatus('startup.bases');
        }
      });

    return () => {
      isDisposed = true;
    };
    // userSettings is intentionally omitted — boot runs once when settings finish loading.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    clearStatus,
    isEmailVerified,
    isUserSettingsLoading,
    setActiveBase,
    setStatus,
    user?.key,
  ]);

  // Hydrate the last-selected (or first) roster base as soon as the rail list is ready.
  // This is the restore path that used to live in product-line reconcile.
  useEffect(() => {
    if (!user?.key || !isEmailVerified || isUserSettingsLoading || !rosterHasLoaded) {
      return;
    }

    if (activeBaseKeyRef.current) {
      clearStatus('startup.bases');
      return;
    }

    const nextBase = pickRestoredBase(rosterBases, getPreferredBaseKey(userSettings));
    const nextKey = getBaseKey(nextBase);
    if (!nextKey) {
      clearStatus('startup.bases');
      return;
    }

    setActiveBase((previous) => applyResolvedBase(previous, nextBase));
    clearStatus('startup.bases');
  }, [
    clearStatus,
    isEmailVerified,
    isUserSettingsLoading,
    rosterBases,
    rosterHasLoaded,
    setActiveBase,
    user?.key,
    userSettings,
  ]);

  useEffect(() => {
    if (!user?.key || !isEmailVerified) {
      return undefined;
    }

    if (isUserSettingsLoading && !activeBaseKeyRef.current && !getPreferredBaseKey(userSettings)) {
      return undefined;
    }

    const baseKey = activeBaseKeyRef.current;
    if (!baseKey) {
      return undefined;
    }

    let isDisposed = false;

    const unsubscribeBase = subscribeToBase(baseKey, (nextBase) => {
      if (isDisposed) return;

      setActiveBase((previous) => (
        mergeBaseUpdate(previous, nextBase, baseKey)
      ));
    });

    return () => {
      isDisposed = true;
      if (unsubscribeBase) unsubscribeBase();
    };
    // Subscribe only to the in-memory active base.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeBase?.key,
    isEmailVerified,
    isUserSettingsLoading,
    setActiveBase,
    user?.key,
  ]);
};

export default usePreloadActiveBase;
